import React, { useEffect, useMemo, useState } from 'react';
import { lang } from '@umu/ukit-lang';
import { If } from 'common/src/components';
import { Button, Checkbox, Table } from '@umu/umu-design';
import { useSelections } from 'ahooks';
import {
    ActType,
    IMultiCapabilityTemplateInfo,
    ITemplateComponentProps,
    ITemplateQuestionInfo,
    PanelType,
} from '../../../interface/multiCapabilityTemplate';
import { PaginationV2 } from '../../../../pagination/index';
import { onWeights } from '../../../dataFlow';
import { getTemplateQuestionListByType } from '../../../server/template';
import { TemplateType } from '../../../interface/multiCapabilityTemplate';
import { renderTooltip } from '../../dataFlow/element';

const defaultPageInfo: IPageInfo = {
    current_page: 1,
    list_total_num: 0,
    total_num: 0,
    size: 10,
    total_page_num: 1,
};

const BodyContent = (props: ITemplateComponentProps) => {
    const {
        dataSource: { listSelectInfo },
        onCommunication,
    } = props;
    const { info } = listSelectInfo || {};
    const [pageInfo, setPageInfo] = useState<IPageInfo>(defaultPageInfo);
    const [list, setList] = useState<IMultiCapabilityTemplateInfo[]>([]); // 列表数据
    const { selected, allSelected, isSelected, toggle, toggleAll, partiallySelected } = useSelections(
        list.map((item) => item.id),
    );
    const selectWeightsList = useMemo(() => {
        return list
            .filter((item) => selected.includes(item.id))
            .map((item) => {
                return { weights: Number(item.weights) };
            });
    }, [selected, list]);
    const columns = [
        {
            title: <Checkbox checked={allSelected} onClick={toggleAll} indeterminate={partiallySelected} />,
            dataIndex: 'id',
            key: 'id',
            width: 50,
            render: (_text: string, record: IMultiCapabilityTemplateInfo) => {
                return <Checkbox checked={isSelected(record.id)} onClick={() => toggle(record.id)} />;
            },
        },
        {
            title: lang('序号'),
            dataIndex: 'index',
            key: 'index',
            width: 100,
            render: (text: number) => {
                return text;
            },
        },
        {
            title: lang('名称'),
            dataIndex: 'title',
            key: 'title',
            width: 150,
            render: renderTooltip,
        },
        {
            title: lang('关联问题'),
            dataIndex: 'list',
            key: 'list',
            width: 200,
            render: (questionList: ITemplateQuestionInfo[]) => {
                const [questionInfo = { title: '' }] = questionList;
                return renderTooltip(questionInfo?.title || '');
            },
        },
        {
            title: lang('描述'),
            dataIndex: 'desc',
            key: 'desc',
            width: 150,
            render: renderTooltip,
        },
        {
            title: lang('权重'),
            dataIndex: 'weights',
            key: 'weights',
            width: 100,
            render: (weights: string, record: IMultiCapabilityTemplateInfo) => {
                return isSelected(record.id) ? onWeights(Number(weights), selectWeightsList) : '-';
            },
        },
    ];
    const onInit = () => {
        if (!info || info.id === '') {
            return;
        }
        getTemplateQuestionListByType({
            parent_type: TemplateType.ENTERPRISE,
            id: info?.id,
            get_base: 0,
            evaluation_criteria_id: info?.evaluation_criteria_id || '',
        }).then((res) => {
            const infoList = res?.list || [];
            const newPageInfo = {
                ...pageInfo,
                total_page_num: Math.ceil(infoList.length / pageInfo.size) || 1,
                list_total_num: infoList.length,
            };
            const ids = (info?.list || []).map((item) => item?.id);

            const selectList = infoList.filter((item) => ids.includes(item.id));
            setList(infoList as unknown as IMultiCapabilityTemplateInfo[]);
            setPageInfo(newPageInfo);
            const tmpList = selectList && selectList.length > 0 ? selectList : infoList;
            if (tmpList.length > 0) {
                tmpList.forEach((item) => {
                    toggle(item.id);
                });
            }
        });
    };

    useEffect(() => {
        onInit();
    }, []);
    const HeardTitle = (
        <div className="label-normal">
            {lang('当前多维度评价')}: <span>{info?.title}</span>
        </div>
    );
    const onPageChange = (page: number) => {
        const newPageInfo = {
            ...pageInfo,
            current_page: page,
        };
        setPageInfo(newPageInfo);
    };
    const getTableData = () => {
        const start = (pageInfo.current_page - 1) * pageInfo.size;
        const end = pageInfo.current_page * pageInfo.size;
        return list.slice(start, end);
    };
    const onCancelClick = () => {
        onCommunication(ActType.PANEL_CANCEL, { info: {} });
    };
    const onPreClick = () => {
        onCommunication(ActType.UPDATE_PANEL_TYPE, {
            info: {
                panelType: PanelType.LIST,
                isShowBack: false,
            },
        });
    };
    const onNextClick = () => {
        const templateInfo = {
            ...info,
            parent_type: TemplateType.ENTERPRISE,
            list: list.filter((item) => selected.includes(item.id)),
        };
        onCommunication(ActType.UPDATE_PANEL_TYPE, {
            info: {
                panelType: PanelType.EDIT,
                isShowBack: true,
                title: lang('设置多维度评价-编辑关联问题及描述'),
                previewPanelType: [PanelType.SELECT_CAPABILITY_DETAIL],
                templateInfo,
                editorConfig: {
                    isShowPreStep: true,
                    isShowPreview: true,
                    isShowCancel: true,
                    title: HeardTitle,
                },
                dataSource: {
                    ...props.dataSource,
                    listSelectInfo: {
                        templateType: TemplateType.ENTERPRISE,
                        info: templateInfo,
                    },
                },
            },
        });
    };
    return (
        <div className="template-capability-list">
            <div className="capability-flex-col capability-heard capability-space-between">
                {HeardTitle}
                <div className="desc-info">
                    {(lang('已选${0}条') || '').replace('${0}', `${selected.length}/${list.length}`)}
                </div>
            </div>
            <div className="template-list">
                <Table columns={columns} data={getTableData()} scroll={{ y: 280 }} emptyText={' '} />
                <If condition={list.length > 0}>
                    <div className="capability-flex-col fix-right small-rows">
                        <div className="total-info">{lang('共 {$0{0= 条,1= 条}}', list.length)}</div>
                        <If condition={pageInfo.total_page_num > 1}>
                            <div className="btn-split">
                                <PaginationV2
                                    showSizeChanger
                                    currentPage={pageInfo.current_page}
                                    maxPage={pageInfo.total_page_num}
                                    onPageChange={onPageChange}
                                />
                            </div>
                        </If>
                    </div>
                </If>
            </div>
            <div className="footer-list capability-flex-col fix-right">
                <Button type="ghost-grey" onClick={onCancelClick}>
                    {lang('取消')}
                </Button>
                <Button type="ghost-grey" className="btn-split" onClick={onPreClick}>
                    {lang('上一步')}
                </Button>
                <Button type="primary" disabled={selected.length <= 0} className="btn-split" onClick={onNextClick}>
                    {lang('下一步')}
                </Button>
            </div>
        </div>
    );
};
export const templateCapabilitySelect = {
    BodyContent,
};
